import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/stores";
import { addWsNotification } from "@/slides/notificationBell/notificationBellSlice";
import { WsNotification } from "@/dto/notifications/response/NotificationResponse.d";
import sessionUtil from "@/utils/sessionUtil";
import env from "@/env";

// ─── Timing constants ────────────────────────────────────────────────────────

/** Ping interval — keeps Nginx from closing the idle connection (nginx default
 *  proxy_read_timeout is 60 s; sending every 30 s gives a comfortable margin). */
const HEARTBEAT_MS = 30_000;

/** Exponential-backoff base delay for reconnects. */
const BASE_DELAY_MS = 1_000;   // 1 s on the first retry

/** Hard cap — never wait longer than this between attempts. */
const MAX_DELAY_MS = 30_000;   // 30 s

/** If the connection stays open for this long, the server is healthy.
 *  Reset the backoff counter so the next disconnect starts fresh. */
const STABLE_AFTER_MS = 15_000; // 15 s

// ─── Backoff helper ──────────────────────────────────────────────────────────

/**
 * Returns the next retry delay using binary-exponential backoff with ±20 % jitter.
 *
 * attempt=0 → ~1 s
 * attempt=1 → ~2 s
 * attempt=2 → ~4 s
 * attempt=3 → ~8 s
 * attempt=4 → ~16 s
 * attempt≥5 → ~30 s (capped)
 *
 * Jitter prevents the "thundering herd" problem when Nginx restarts and all
 * connected tabs try to reconnect simultaneously.
 */
const backoffDelay = (attempt: number): number => {
  const base = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
  const jitter = base * 0.2 * (Math.random() * 2 - 1); // ±20 %
  return Math.round(base + jitter);
};

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Connects to the real-time notification WebSocket and keeps it alive.
 *
 * Reconnect strategy:
 *   • Exponential backoff with ±20 % jitter (1 s → 2 s → 4 s … → 30 s cap)
 *   • Backoff resets after the connection has been stable for 15 s
 *   • Intentional close (code 1000, e.g. logout / component unmount) is NOT retried
 *
 * Heartbeat:
 *   • Sends "ping" every 30 s so Nginx proxy_read_timeout is never hit
 */
const useNotificationWebSocket = () => {
  const dispatch = useDispatch<AppDispatch>();

  const socketRef    = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stableRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryRef     = useRef(0);   // current backoff attempt counter
  const mountedRef   = useRef(true);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const clearHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const clearReconnect = () => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
  };

  const clearStableTimer = () => {
    if (stableRef.current) {
      clearTimeout(stableRef.current);
      stableRef.current = null;
    }
  };

  // ── Core connect ─────────────────────────────────────────────────────────

  const connect = () => {
    if (!mountedRef.current) return;

    const sessionId = sessionUtil.getSession();
    if (!sessionId) return; // not logged in yet

    const apiHost = env.REACT_APP_API_HOST || "";
    const wsHost  = apiHost
      .replace(/^https:\/\//, "wss://")
      .replace(/^http:\/\//, "ws://");
    const wsUrl = `${wsHost}/notification-write-service/ws/notifications?sessionId=${sessionId}`;

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    // ── onopen ─────────────────────────────────────────────────────────────
    socket.onopen = () => {
      const attempt = retryRef.current;
      console.log(
        `[Notification WS] Connected${attempt > 0 ? ` (retry #${attempt})` : ""}`,
      );

      // Start heartbeat
      heartbeatRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send("ping");
        }
      }, HEARTBEAT_MS);

      // After STABLE_AFTER_MS of uptime, reset the backoff counter
      stableRef.current = setTimeout(() => {
        if (retryRef.current > 0) {
          console.log("[Notification WS] Connection stable — backoff reset");
          retryRef.current = 0;
        }
      }, STABLE_AFTER_MS);
    };

    // ── onmessage ──────────────────────────────────────────────────────────
    socket.onmessage = (event) => {
      if (event.data === "pong") return; // heartbeat reply — ignore
      try {
        const payload: WsNotification = JSON.parse(event.data);
        dispatch(addWsNotification(payload));
      } catch {
        // Non-JSON frame (e.g. protocol handshake text) — ignore
      }
    };

    // ── onclose ────────────────────────────────────────────────────────────
    socket.onclose = (event) => {
      clearHeartbeat();
      clearStableTimer();

      // Code 1000 = intentional close (unmount / logout) — do not retry
      if (!mountedRef.current || event.code === 1000) return;

      const delay = backoffDelay(retryRef.current);
      retryRef.current += 1;

      console.log(
        `[Notification WS] Closed (code ${event.code}) — ` +
        `retry #${retryRef.current} in ${delay} ms`,
      );

      reconnectRef.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    };

    // ── onerror ────────────────────────────────────────────────────────────
    socket.onerror = () => {
      // onclose will always fire after onerror — handle retry there
      // console.error("[Notification WS] Error");
    };
  };

  // ── Mount / unmount ───────────────────────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      clearHeartbeat();
      clearReconnect();
      clearStableTimer();
      if (socketRef.current) {
        socketRef.current.close(1000, "Component unmounted");
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useNotificationWebSocket;
